/* ===========================================================================
   Shared motion utilities.

   Every helper returns a teardown function, so components can hand it
   straight back from useEffect and never leak a tween or a listener.
   =========================================================================== */

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export { gsap, ScrollTrigger };

const REDUCE_QUERY = "(prefers-reduced-motion: reduce)";

export function prefersReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia(REDUCE_QUERY).matches;
}

export function isTouch() {
  return typeof window !== "undefined" && !window.matchMedia("(pointer: fine)").matches;
}

/** Standard tween shape, so every reveal on the page shares one rhythm. */
export const REVEAL = { duration: 0.9, ease: "power3.out" };

/**
 * Fade + rise on scroll. Accepts an element or an array of elements; arrays
 * are staggered.
 *
 * Deliberately `fromTo`, never `from`. `from` infers its end values from the
 * element's state at creation time, so under React StrictMode — where every
 * effect is mounted, cleaned up and mounted again — the second pass reads the
 * opacity 0 the first pass left behind and animates 0 to 0. Stating both ends
 * explicitly, and reverting rather than killing on cleanup, makes the helper
 * safe to run twice.
 */
export function revealUp(targets, options = {}) {
  const list = (Array.isArray(targets) ? targets : [targets]).filter(Boolean);
  if (!list.length) return () => {};

  const { y = 34, stagger = 0.08, start = "top 85%", delay = 0, scale = 1, trigger } = options;

  if (prefersReducedMotion()) {
    gsap.set(list, { opacity: 1, y: 0, scale: 1, clearProps: "transform" });
    return () => {};
  }

  const tween = gsap.fromTo(
    list,
    { y, opacity: 0, scale },
    {
      ...REVEAL,
      y: 0,
      opacity: 1,
      scale: 1,
      stagger,
      delay,
      scrollTrigger: { trigger: trigger || list[0], start, once: true },
    },
  );

  return () => {
    tween.scrollTrigger?.kill();
    tween.revert();
  };
}

/**
 * Wipe an image in behind a clip-path while it eases back to scale 1 —
 * the reveal reads as the photo being uncovered rather than sliding around.
 */
export function revealImage(el, options = {}) {
  if (!el) return () => {};
  const { start = "top 88%", from = "inset(0 0 100% 0)", delay = 0 } = options;

  if (prefersReducedMotion()) {
    gsap.set(el, { clipPath: "none", opacity: 1, scale: 1 });
    return () => {};
  }

  const tl = gsap.timeline({
    scrollTrigger: { trigger: el, start, once: true },
    delay,
  });

  tl.fromTo(
    el,
    { clipPath: from, scale: 1.12 },
    { clipPath: "inset(0 0 0% 0)", scale: 1, duration: 1.15, ease: "power3.out" },
  );

  return () => {
    tl.scrollTrigger?.kill();
    tl.revert();
  };
}

/**
 * Wrap each word (and optionally each character) of an element's text in
 * spans so they can be animated independently. Returns the created nodes
 * plus a restore function.
 */
export function splitText(el, mode = "words") {
  if (!el) return { units: [], restore: () => {} };

  const original = el.innerHTML;
  const words = el.textContent.split(/(\s+)/);
  const units = [];

  el.textContent = "";

  words.forEach((chunk) => {
    if (/^\s+$/.test(chunk)) {
      el.appendChild(document.createTextNode(" "));
      return;
    }
    const wordEl = document.createElement("span");
    wordEl.className = "split-word";

    if (mode === "chars") {
      Array.from(chunk).forEach((ch) => {
        const inner = document.createElement("span");
        inner.className = "split-char";
        inner.textContent = ch;
        wordEl.appendChild(inner);
        units.push(inner);
      });
    } else {
      const inner = document.createElement("span");
      inner.className = "split-inner";
      inner.textContent = chunk;
      wordEl.appendChild(inner);
      units.push(inner);
    }

    el.appendChild(wordEl);
  });

  return {
    units,
    restore: () => {
      el.innerHTML = original;
    },
  };
}

/**
 * Pull an element gently toward the cursor. Used on the hero quote card and
 * the course cards' arrow affordance — never on body text.
 */
export function magnetic(el, options = {}) {
  if (!el || isTouch() || prefersReducedMotion()) return () => {};

  const { strength = 0.14, scale = 1 } = options;
  const move = gsap.quickTo(el, "x", { duration: 0.5, ease: "power3.out" });
  const moveY = gsap.quickTo(el, "y", { duration: 0.5, ease: "power3.out" });

  const onMove = (event) => {
    const rect = el.getBoundingClientRect();
    move((event.clientX - (rect.left + rect.width / 2)) * strength);
    moveY((event.clientY - (rect.top + rect.height / 2)) * strength);
  };

  const onEnter = () => scale !== 1 && gsap.to(el, { scale, duration: 0.4, ease: "power3.out" });
  const onLeave = () => {
    move(0);
    moveY(0);
    if (scale !== 1) gsap.to(el, { scale: 1, duration: 0.5, ease: "power3.out" });
  };

  el.addEventListener("mousemove", onMove);
  el.addEventListener("mouseenter", onEnter);
  el.addEventListener("mouseleave", onLeave);

  return () => {
    el.removeEventListener("mousemove", onMove);
    el.removeEventListener("mouseenter", onEnter);
    el.removeEventListener("mouseleave", onLeave);
    gsap.killTweensOf(el);
  };
}

/**
 * Subtle pointer-driven tilt. Depth is small on purpose: the brand is flat
 * vector work, so the card should suggest a plane, not a 3D object.
 */
export function tilt(el, options = {}) {
  if (!el || isTouch() || prefersReducedMotion()) return () => {};

  const { max = 5, lift = -6, glareSelector } = options;
  const glare = glareSelector ? el.querySelector(glareSelector) : null;

  const setX = gsap.quickTo(el, "rotationY", { duration: 0.5, ease: "power3.out" });
  const setY = gsap.quickTo(el, "rotationX", { duration: 0.5, ease: "power3.out" });

  const onMove = (event) => {
    const rect = el.getBoundingClientRect();
    const px = (event.clientX - rect.left) / rect.width - 0.5;
    const py = (event.clientY - rect.top) / rect.height - 0.5;
    setX(px * max * 2);
    setY(-py * max * 2);
    if (glare) {
      gsap.to(glare, {
        opacity: 0.5,
        x: px * rect.width * 0.6,
        y: py * rect.height * 0.6,
        duration: 0.5,
        ease: "power3.out",
      });
    }
  };

  const onEnter = () => gsap.to(el, { y: lift, duration: 0.45, ease: "power3.out" });

  const onLeave = () => {
    setX(0);
    setY(0);
    gsap.to(el, { y: 0, duration: 0.55, ease: "power3.out" });
    if (glare) gsap.to(glare, { opacity: 0, duration: 0.4 });
  };

  el.addEventListener("mousemove", onMove);
  el.addEventListener("mouseenter", onEnter);
  el.addEventListener("mouseleave", onLeave);

  return () => {
    el.removeEventListener("mousemove", onMove);
    el.removeEventListener("mouseenter", onEnter);
    el.removeEventListener("mouseleave", onLeave);
    gsap.killTweensOf(el);
  };
}

/** Scroll-linked parallax on the y axis. */
export function parallax(el, options = {}) {
  if (!el || prefersReducedMotion()) return () => {};
  const { distance = -60, trigger = el, start = "top bottom", end = "bottom top" } = options;

  const tween = gsap.to(el, {
    y: distance,
    ease: "none",
    scrollTrigger: { trigger, start, end, scrub: true },
  });

  return () => {
    tween.scrollTrigger?.kill();
    tween.kill();
  };
}

/** Count a number up once its element scrolls into view. */
export function countUp(el, target, options = {}) {
  if (!el) return () => {};
  const { duration = 1.8, start = "top 88%" } = options;

  if (prefersReducedMotion()) {
    el.textContent = String(target);
    return () => {};
  }

  const state = { value: 0 };
  const tween = gsap.to(state, {
    value: target,
    duration,
    ease: "power2.out",
    onUpdate: () => {
      el.textContent = String(Math.round(state.value));
    },
    scrollTrigger: { trigger: el, start, once: true },
  });

  return () => {
    tween.scrollTrigger?.kill();
    tween.kill();
  };
}
